import PropTypes from 'prop-types';
import { useFormContext, Controller } from 'react-hook-form';
import { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Button,
    Card,
    IconButton,
    Stack,
    TextField,
    Typography,
    Grid,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import Iconify from '../iconify';

import RHFAutocompleteAsync from './rhf-autocomplete-async';
import RHFAutocompleteWithQuickAdd from './rhf-autocomplete-with-quick-add';

/**
 * RHFArrayField - React Hook Form component for dynamic array fields
 * Allows adding/removing items in an array with configurable fields
 */
export default function RHFArrayField({
    name,
    label = 'Items',
    fields = [],
    minItems = 1,
    maxItems,
    defaultItem = {},
    EntityCreationDialog,
}) {
    const { control, setValue, watch } = useFormContext();
    const watchedItems = watch(name);
    const fullFormData = watch();

    const effectiveMinItems = typeof minItems === 'number' ? minItems : (parseInt(minItems, 10) || 1);
    const effectiveMaxItems = typeof maxItems === 'number' ? maxItems : (parseInt(maxItems, 10) || undefined);

    // Normalize for rendering (ensuring at least minItems)
    const items = useMemo(() => {
        if (!watchedItems || !Array.isArray(watchedItems) || watchedItems.length < effectiveMinItems) {
            const currentLength = watchedItems ? watchedItems.length : 0;
            const missingCount = effectiveMinItems - currentLength;
            const tempItems = watchedItems ? [...watchedItems] : [];
            for (let i = 0; i < missingCount; i += 1) {
                const isFirst = tempItems.length === 0;
                tempItems.push({
                    ...defaultItem,
                    ...(isFirst && defaultItem.isHeadOffice !== undefined ? { isHeadOffice: true } : {}),
                });
            }
            return tempItems;
        }
        return watchedItems;
    }, [watchedItems, effectiveMinItems, defaultItem]);

    // Synchronize default value in form state on mount/init
    useEffect(() => {
        const currentItems = watch(name);
        if (!currentItems || !Array.isArray(currentItems) || currentItems.length < effectiveMinItems) {
            const currentLength = currentItems ? currentItems.length : 0;
            const missingCount = effectiveMinItems - currentLength;
            const newItems = currentItems ? [...currentItems] : [];
            for (let i = 0; i < missingCount; i += 1) {
                const isFirst = newItems.length === 0;
                newItems.push({
                    ...defaultItem,
                    ...(isFirst && defaultItem.isHeadOffice !== undefined ? { isHeadOffice: true } : {}),
                });
            }
            setValue(name, newItems);
        }
    }, [name, effectiveMinItems, setValue]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleAddItem = () => {
        if (effectiveMaxItems && items.length >= effectiveMaxItems) return;
        setValue(name, [...items, { ...defaultItem }]);
    };

    const handleRemoveItem = (index) => {
        if (items.length <= effectiveMinItems) return;
        const remaining = items.filter((_, i) => i !== index);
        // If the removed item was head office, ensure the first remaining item is marked as head office
        if (items[index]?.isHeadOffice && remaining.length > 0 && !remaining.some(it => it.isHeadOffice)) {
            remaining[0].isHeadOffice = true;
        }
        setValue(name, remaining);
    };

    const handleItemChange = (index, fieldName, value) => {
        const newItems = items.map((item, i) => {
            if (i === index) {
                return { ...item, [fieldName]: value };
            }
            // If marking an item as Head Office (exclusive checkbox), unmark other items
            if (fieldName === 'isHeadOffice' && value === true) {
                return { ...item, [fieldName]: false };
            }
            return item;
        });

        // Ensure at least one item remains head office if all became false
        if (fieldName === 'isHeadOffice' && value === false && !newItems.some((it) => it[fieldName])) {
            newItems[0][fieldName] = true;
        }

        setValue(name, newItems, { shouldDirty: true, shouldValidate: true });
    };

    // Effect for computeValue logic (e.g. auto-selecting head office)
    useEffect(() => {
        if (fields.some(f => f.computeValue)) {
            fields.forEach(field => {
                if (field.computeValue) {
                    items.forEach((item, index) => {
                        const computed = field.computeValue(item[field.name], fullFormData, index);
                        if (computed !== item[field.name]) {
                            // Use full path setValue to avoid overwriting whole array if possible, 
                            // but handleItemChange does full array. 
                            // Let's use specific path setValue for better performance and to avoid race conditions
                            setValue(`${name}[${index}].${field.name}`, computed);
                        }
                    });
                }
            });
        }
    }, [items.length]); // eslint-disable-line react-hooks/exhaustive-deps
    // Note: We avoid adding fullFormData to deps to prevent infinite loops if computeValue returns new objects.
    // We assume computeValue depends mainly on array structure or specific triggers.

    const renderField = (field, item, index) => {
        const value = item[field.name] || '';

        // Evaluate disabled state
        const isDisabled = typeof field.disabled === 'function'
            ? field.disabled(fullFormData, null, index)
            : field.disabled;

        switch (field.type) {
            case 'autocomplete': {
                // Resolve API endpoint here to prevent unnecessary re-fetches
                // logic: if it returns a string, it only changes when dependencies change
                const resolvedEndpoint = typeof field.apiEndpoint === 'function'
                    ? field.apiEndpoint(fullFormData, index)
                    : field.apiEndpoint;

                if (field.allowQuickAdd && field.quickAddConfig) {
                    return (
                        <RHFAutocompleteWithQuickAdd
                            name={`${name}[${index}].${field.name}`}
                            label={field.label}
                            placeholder={field.placeholder}
                            apiEndpoint={resolvedEndpoint}
                            optionLabel={field.optionLabel}
                            optionValue={field.optionValue}
                            required={field.required}
                            disabled={isDisabled}
                            quickAddConfig={field.quickAddConfig}
                            EntityCreationDialog={EntityCreationDialog}
                            dependsOn={field.dependsOn ? `${name}[${index}].${field.dependsOn}` : undefined}
                        />
                    );
                }

                return (
                    <RHFAutocompleteAsync
                        name={`${name}[${index}].${field.name}`}
                        label={field.label}
                        placeholder={field.placeholder}
                        apiEndpoint={resolvedEndpoint}
                        optionLabel={field.optionLabel}
                        optionValue={field.optionValue}
                        required={field.required}
                        disabled={isDisabled}
                        dependsOn={field.dependsOn ? `${name}[${index}].${field.dependsOn}` : undefined}
                    />
                );
            }

            case 'select':
                return (
                    <TextField
                        fullWidth
                        select
                        label={field.label}
                        value={value}
                        onChange={(e) => handleItemChange(index, field.name, e.target.value)}
                        required={field.required}
                        disabled={isDisabled}
                        SelectProps={{ native: true }}
                    >
                        <option value="">Select...</option>
                        {field.options?.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </TextField>
                );

            case 'checkbox':
                return (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={value || false}
                                onChange={(e) => handleItemChange(index, field.name, e.target.checked)}
                                disabled={isDisabled}
                            />
                        }
                        label={field.label}
                    />
                );

            case 'number':
                return (
                    <TextField
                        fullWidth
                        type="number"
                        label={field.label}
                        value={value}
                        onChange={(e) => handleItemChange(index, field.name, e.target.value)}
                        required={field.required}
                        disabled={isDisabled}
                    />
                );

            case 'date':
                return (
                    <DatePicker
                        label={field.label}
                        value={value ? new Date(value) : null}
                        onChange={(newValue) => {
                            handleItemChange(index, field.name, newValue ? newValue.toISOString() : null);
                        }}
                        disabled={isDisabled}
                        slotProps={{
                            textField: {
                                fullWidth: true,
                                required: field.required,
                                error: false, // Since validation happens at parent
                            }
                        }}
                    />
                );

            case 'time':
                return (
                    <TimePicker
                        label={field.label}
                        value={value ? new Date(value) : null}
                        onChange={(newValue) => {
                            handleItemChange(index, field.name, newValue ? newValue.toISOString() : null);
                        }}
                        disabled={isDisabled}
                        slotProps={{
                            textField: {
                                fullWidth: true,
                                required: field.required,
                                error: false,
                            }
                        }}
                    />
                );

            default: // text
                return (
                    <TextField
                        fullWidth
                        label={field.label}
                        value={value}
                        onChange={(e) => handleItemChange(index, field.name, e.target.value)}
                        required={field.required}
                        disabled={isDisabled}
                    />
                );
        }
    };




    return (
        <Controller
            name={name}
            control={control}
            render={() => (
                <Box>
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                        {label}
                    </Typography>
                    <Stack spacing={2}>
                        {items.map((item, index) => (
                            <Card key={index} sx={{ p: 2, bgcolor: 'background.neutral' }}>
                                <Stack spacing={2}>
                                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                                        <Typography variant="subtitle2">
                                            {label} {index + 1}
                                        </Typography>
                                        {items.length > effectiveMinItems && (
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleRemoveItem(index)}
                                            >
                                                <Iconify icon="solar:trash-bin-trash-bold" />
                                            </IconButton>
                                        )}
                                    </Stack>

                                    <Grid container spacing={2}>
                                        {fields.map((field) => {
                                            const gridProps = field.gridProps || {
                                                xs: 12,
                                                md: Math.max(2, Math.min(12, Math.floor(12 / fields.length)))
                                            };
                                            return (
                                                <Grid item key={field.name} {...gridProps}>
                                                    {renderField(field, item, index)}
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                </Stack>
                            </Card>
                        ))}
                    </Stack>

                    {(!effectiveMaxItems || items.length < effectiveMaxItems) && (
                        <Button
                            variant="outlined"
                            startIcon={<Iconify icon="mingcute:add-line" />}
                            onClick={handleAddItem}
                            sx={{ mt: 2 }}
                        >
                            Add {label}
                        </Button>
                    )}
                </Box>
            )}
        />
    );
}

RHFArrayField.propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string,
    fields: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            type: PropTypes.string.isRequired,
            required: PropTypes.bool,
            options: PropTypes.array,
            gridProps: PropTypes.object,
        })
    ).isRequired,
    minItems: PropTypes.number,
    maxItems: PropTypes.number,
    defaultItem: PropTypes.object,
    EntityCreationDialog: PropTypes.elementType,
};
